import { useEffect, useState } from "react";
import DownBlueButton from "../../../UI/common/Buttons/down-blue-button";
import NickNameTextBox from "../../../UI/common/Inputs/nickname-textbox";
import RegisterTextBox from "../../../UI/common/Inputs/register-textbox";
import classes from "./register-form.module.css";
import RegisterUserInfoForm from "../../profile/components/register-user-info-form";
import { Form, useActionData, useNavigation } from "react-router-dom";
import { devbackendUrl } from "../../../utils/constants";
import AuthErrorModal from "./auth-error-modal";
import type { AuthActionData } from "../utils/auth-action-errors";

// Validation Functions

const validateUsername = (value: string) => {
  if (!value) return { isValid: false, errorMessage: "Username is required" };
  const isValid = value.trim().length > 3;
  return { isValid, errorMessage: isValid ? "" : "Username must be at least 4 characters" };
};

const validateEmail = (value: string) => {
  if (!value) return { isValid: false, errorMessage: "Email is required" };
  const isValid = /\S+@\S+\.\S+/.test(value);
  return { isValid, errorMessage: isValid ? "" : "Invalid email address" };
};

const validatePassword = (value: string) => {
  if (!value) return { isValid: false, errorMessage: "Password is required" };
  const isValid = value.length > 7;
  return { isValid, errorMessage: isValid ? "" : "Password must be more than 7 characters" };
};

const validateConfirmPassword = (value: string, password: string) => {
  if (!value) return { isValid: false, errorMessage: "Confirmation is required" };
  const isValid = value === password;
  return { isValid, errorMessage: isValid ? "" : "Passwords do not match" };
};

interface RegisterFormProps {
  showCurrentForm: boolean;
  setShowCurrentForm: (value: boolean) => void;
}
const RegisterForm: React.FC<RegisterFormProps> = (props) => {
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [isUsernameValid, setIsUsernameValid] = useState(false);
  const [usernameAvailability, setUsernameAvailability] = useState<'idle' | 'checking' | 'available' | 'taken' | 'error'>('idle');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isConfirmPasswordValid, setIsConfirmPasswordValid] = useState(false);
  const [isErrorDismissed, setIsErrorDismissed] = useState(false);

  const data = useActionData() as AuthActionData | undefined;
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";
  const handleValidationChange = (setter: React.Dispatch<React.SetStateAction<boolean>>) => {
    return (isValid: boolean, reset: () => void) => {
      setter(isValid);
    };
  };

  const handleUsernameValidationChange = (isValid: boolean, reset: () => void, value?: string) => {
    setIsUsernameValid(isValid);
    setUsername(value ?? "");
  };

  const handlePasswordValidationChange = (isValid: boolean, reset: () => void, value?: string) => {
    setIsPasswordValid(isValid);
    setPassword(value ?? "");
  };

  const handleConfirmPasswordValidationChange = (isValid: boolean, reset: () => void) => {
    setIsConfirmPasswordValid(isValid);
  };

  useEffect(() => {
    const trimmedUsername = username.trim();

    if (!isUsernameValid || trimmedUsername.length < 4) {
      setUsernameAvailability("idle");
      return;
    }

    setUsernameAvailability("checking");
    const controller = new AbortController();
    let isActive = true;
    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await fetch(`${devbackendUrl}auth/username-availability?username=${encodeURIComponent(trimmedUsername)}`, {
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error("Unable to check username");
        }
        const data = await response.json();
        if (isActive) {
          setUsernameAvailability(data.available ? "available" : "taken");
        }
      } catch (error) {
        if (!isActive) return;
        console.error("Failed to check username availability:", error);
        setUsernameAvailability("error");
      }
    }, 450);

    return () => {
      isActive = false;
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [username, isUsernameValid]);

  const isFormValid =
    isUsernameValid &&
    usernameAvailability === "available" &&
    isEmailValid &&
    isPasswordValid &&
    isConfirmPasswordValid;

  const actionError = data?.error;
  const backendMessage = actionError?.message ?? data?.message;
  const availabilityErrorMessage = usernameAvailability === "error"
    ? "We could not check username availability because the server is not reachable right now. Please make sure the backend is running, then try again."
    : null;
  const modalMessage = backendMessage ?? availabilityErrorMessage;
  const errorTitle = backendMessage ? (actionError?.title ?? "Sign up failed") : "Connection issue";
  const usernameMessage = usernameAvailability === "taken"
    ? "Username is already taken"
    : backendMessage;
  const usernameStatusText = usernameAvailability === "checking"
    ? "Checking username..."
    : usernameAvailability === "available"
      ? "Username is available"
      : usernameAvailability === "error"
        ? "Could not check username right now"
        : "";

  useEffect(() => {
    if (modalMessage) {
      setIsErrorDismissed(false);
    }
  }, [modalMessage]);

  // const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
  //   event.preventDefault();
  //   if (!isFormValid) return;

  //   props.setShowCurrentForm(false); // Trigger animation
  //   props.onSubmit(); // Trigger the onSubmit handler
  // };
  return (
    <div className={classes.container}>
      {modalMessage && !isErrorDismissed && (
        <AuthErrorModal
          title={errorTitle}
          message={modalMessage}
          onClose={() => setIsErrorDismissed(true)}
        />
      )}
      {props.showCurrentForm ? (
        <Form method="POST"
          className={`${classes.form} ${!props.showCurrentForm && classes.fadeOut}`}
        // onSubmit={handleSubmit}
        >
          <div className={classes.heading}>
            <h1>Welcome</h1>
            <NickNameTextBox
              type="text"
              id="nickname"
              placeholder="...Enter your nickname..."
            />
            <h1>!</h1>
          </div>
          <div className={classes.wrapper}>
            <div>
              <RegisterTextBox
                type="text"
                id="username"
                placeholder="Enter your name"
                validateFunction={validateUsername}
                onValidationChange={handleUsernameValidationChange}
                InvalidMessage={usernameMessage}
              />
              {usernameStatusText && (
                <p className={`${classes.usernameStatus} ${usernameAvailability === "available" ? classes.availableStatus : classes.pendingStatus}`}>
                  {usernameStatusText}
                </p>
              )}
            </div>
            <div>
              <RegisterTextBox
                type="text"
                id="email"
                placeholder="Enter your email"
                validateFunction={validateEmail}
                onValidationChange={handleValidationChange(setIsEmailValid)}
                InvalidMessage={backendMessage}
              />
            </div>
            <div>
              <RegisterTextBox
                type="password"
                id="password"
                placeholder="Enter your password"
                validateFunction={(value) => validatePassword(value)}
                onValidationChange={(isValid, reset, value) => handlePasswordValidationChange(isValid, reset, value)}
              />
            </div>
            <div>
              <RegisterTextBox
                type="password"
                id="confirm-password"
                placeholder="Retype your password"
                validateFunction={(value) => validateConfirmPassword(value, password)}
                onValidationChange={handleConfirmPasswordValidationChange}
              />
            </div>
            <DownBlueButton type="submit" isDisabled={!isFormValid || isSubmitting} />
          </div>
        </Form>
      ) : (
        <RegisterUserInfoForm
          parentClassName={classes.newForm}
        />
      )}
    </div>
  );
};

export default RegisterForm;
